/**
 * Очередь запросов на диалог для {@code AppDialogHost} (заказ владельца 2026-08-31).
 *
 * <p><b>Зачем шина.</b> Спросить «вы уверены?» нужно из обычной асинхронной функции —
 * обработчика нажатия, а не из разметки. Нарисовать при этом надо React-компонент в цветах
 * активной темы. Между этими двумя мирами и стоит шина: вызывающий получает промис, хост
 * получает запрос. Тем же способом в проект приходит открытие панели контактов по пушу
 * ({@code openDruzhinaBridge}).
 *
 * <p><b>Почему очередь, а не один запрос.</b> Диалог всегда один на экране, но попросить о
 * втором могут, пока открыт первый: два обработчика, две ошибки подряд. Терять второй нельзя —
 * его ждёт промис, и брошенный без ответа он навсегда подвесил бы вызывающего. Поэтому
 * запросы выстраиваются в очередь и показываются по одному.
 *
 * <p><b>Если хоста нет</b> (он не смонтирован — ранний старт, тест, экзотическая среда),
 * запрос ждёт в очереди. Разруливает это уже {@code confirmDialog}: там у каждого вызова есть
 * запасной путь, и промиса, который никогда не разрешится, не остаётся.
 */

/** Кнопка меню выбора (`choose`). Стили — те же, что у кнопок системного `Alert`. */
export type AppDialogOption = { text: string; style?: 'default' | 'cancel' | 'destructive' };

export type AppDialogRequest = {
  id: number;
  kind: 'confirm' | 'notice' | 'choose';
  title: string;
  body: string;
  /** У `choose` не используется: его кнопки — в `options`. */
  confirmText: string;
  /** Только у `confirm`: у уведомления второй кнопки нет. */
  cancelText?: string;
  destructive?: boolean;
  /** Только у `choose`: варианты по порядку, ответ — индекс нажатого. */
  options?: AppDialogOption[];
};

/**
 * Ответ хоста: да/нет у `confirm` и `notice`, индекс варианта у `choose`, `null` — меню
 * закрыли, ничего не выбрав.
 */
export type AppDialogAnswer = boolean | number | null;

type Listener = (queue: AppDialogRequest[]) => void;

let nextId = 1;
let queue: AppDialogRequest[] = [];
const resolvers = new Map<number, (answer: AppDialogAnswer) => void>();
let listener: Listener | null = null;

function emit(): void {
  listener?.(queue);
}

/** Есть ли кому показать диалог. Вызывающий решает по этому, идти ли запасным путём. */
export function hasAppDialogHost(): boolean {
  return listener !== null;
}

function enqueue(
  request: Omit<AppDialogRequest, 'id'>,
  resolve: (answer: AppDialogAnswer) => void,
): void {
  const id = nextId++;
  resolvers.set(id, resolve);
  queue = [...queue, { ...request, id }];
  emit();
}

export function requestAppDialog(request: Omit<AppDialogRequest, 'id'>): Promise<boolean> {
  return new Promise<boolean>((resolve) => enqueue(request, (answer) => resolve(answer === true)));
}

/** Меню из нескольких вариантов: промис отдаёт индекс нажатого или `null`, если меню закрыли. */
export function requestAppChoice(request: {
  title: string;
  body: string;
  options: AppDialogOption[];
}): Promise<number | null> {
  return new Promise<number | null>((resolve) =>
    enqueue({ ...request, kind: 'choose', confirmText: '' }, (answer) =>
      resolve(typeof answer === 'number' ? answer : null),
    ),
  );
}

/**
 * Ответ на запрос. Повторный ответ на тот же идентификатор игнорируется: хост может закрыться
 * и по кнопке, и по «назад», и оба пути ведут сюда.
 */
export function resolveAppDialog(id: number, answer: AppDialogAnswer): void {
  const resolve = resolvers.get(id);
  if (!resolve) return;
  resolvers.delete(id);
  queue = queue.filter((r) => r.id !== id);
  resolve(answer);
  emit();
}

export function subscribeAppDialogs(fn: Listener): () => void {
  listener = fn;
  // Отдаём накопившееся сразу: запрос мог прийти до того, как хост смонтировался.
  if (queue.length > 0) fn(queue);
  return () => {
    if (listener === fn) listener = null;
  };
}

/**
 * Сброс между тестами. В приложении не вызывается: хост живёт столько же, сколько само
 * приложение, а незакрытых запросов при выходе не остаётся.
 */
export function resetAppDialogsForTests(): void {
  queue = [];
  resolvers.clear();
  listener = null;
  nextId = 1;
}
